import { useCallback, useState } from 'react';

/**
 * useState backed by localStorage under `key` — reads once on mount (lazily,
 * like useState's own functional initializer), writes synchronously on every
 * update. Falls back to `defaultValue` when nothing is stored yet, or when
 * reading/writing throws (private browsing, quota, corrupt JSON).
 */
export function useLocalStorageState<T>(
  key: string,
  defaultValue: T | (() => T),
): [T, (value: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw != null) return JSON.parse(raw) as T;
    } catch {
      // fall through to default
    }
    return defaultValue instanceof Function ? defaultValue() : defaultValue;
  });

  const setPersistedValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved = next instanceof Function ? next(prev) : next;
        try {
          localStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          // ignore (private browsing / quota)
        }
        return resolved;
      });
    },
    [key],
  );

  return [value, setPersistedValue];
}
